
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FeatureFlagPanel } from "@/components/FeatureFlagPanel";
import { BeforeAfterDemo } from "@/components/BeforeAfterDemo";
import Dashboard from "./pages/Dashboard";
import NoteSpace from "./pages/NoteSpace";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import Landing from "./pages/Landing";
import MultiAnalysis from "./pages/MultiAnalysis";

import Admin from "./pages/Admin";
import Resources from "./pages/Resources";
import Blog from "./pages/Blog";
import Contact from "./pages/Contact";
import CookiePolicy from "./pages/CookiePolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on auth errors or 404s
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status;
          if (status === 401 || status === 403 || status === 404) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={
              <ErrorBoundary>
                <Landing />
              </ErrorBoundary>
            } />
            <Route path="/dashboard" element={
              <ErrorBoundary>
                <FeatureFlagPanel />
                <Dashboard />
              </ErrorBoundary>
            } />
            <Route path="/auth" element={
              <ErrorBoundary>
                <Auth />
              </ErrorBoundary>
            } />
            <Route path="/analysis/:analysisId" element={
              <ErrorBoundary>
                <FeatureFlagPanel />
                <MultiAnalysis />
              </ErrorBoundary>
            } />
            <Route path="/space/:spaceId" element={
              <ErrorBoundary>
                <FeatureFlagPanel />
                <NoteSpace />
              </ErrorBoundary>
            } />
            <Route path="/admin" element={
              <ErrorBoundary>
                <Admin />
              </ErrorBoundary>
            } />
            <Route path="/resources" element={
              <ErrorBoundary>
                <Resources />
              </ErrorBoundary>
            } />
            <Route path="/blog" element={
              <ErrorBoundary>
                <Blog />
              </ErrorBoundary>
            } />
            <Route path="/blog/:slug" element={
              <ErrorBoundary>
                <Blog />
              </ErrorBoundary>
            } />
            <Route path="/contact" element={
              <ErrorBoundary>
                <Contact />
              </ErrorBoundary>
            } />
            <Route path="/cookie-policy" element={
              <ErrorBoundary>
                <CookiePolicy />
              </ErrorBoundary>
            } />
            <Route path="/privacy-policy" element={
              <ErrorBoundary>
                <PrivacyPolicy />
              </ErrorBoundary>
            } />
            <Route path="/demo/before-after" element={
              <ErrorBoundary>
                <BeforeAfterDemo />
              </ErrorBoundary>
            } />
            <Route path="*" element={
              <ErrorBoundary>
                <NotFound />
              </ErrorBoundary>
            } />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
